/**
 * @fileoverview Bootstrap module that initializes all Vue plugins and global configuration.
 *
 * This module is responsible for:
 * - Registering Vue plugins (Element UI, Vuelidate, CodeMirror, etc.)
 * - Setting up authentication (Auth0/Keycloak)
 * - Registering global filters and directives
 * - Initializing error tracking (Sentry)
 * - Creating the Web Worker for background processing
 *
 * @module bootstrap
 */

import Vue from "vue";
import _ from "lodash";

// UI Component Libraries
import Viewer from "v-viewer";
import ElementUI from "element-ui";
import VueTimeago from "vue-timeago";
import VueCodeMirror from "vue-codemirror";
import Vuelidate from "vuelidate";
import ReadMore from "vue-read-more";
import VueMasonry from "vue-masonry-css";
import VueDND from "awe-dnd";
import locale from "element-ui/lib/locale/lang/en";
import VueHtml5Editor from "vue-html5-editor";
import ElSearchTablePagination from "el-search-table-pagination";

// Internal modules
import { AuthPlugin } from "./auth";
import KeyReplyUtility from "./plugins/index";
import store from "@/store";
import filters from "@/filters";
import directives from "@/directives";

// Polyfills for browser compatibility
import "core-js/stable";
import "regenerator-runtime/runtime";

// Push notifications library
import "push.js";

// CodeMirror language modes and addons
import "codemirror/mode/htmlmixed/htmlmixed.js";
import "codemirror/mode/javascript/javascript.js";
import "codemirror/mode/yaml/yaml.js";
import "codemirror/addon/selection/active-line.js";
import "codemirror/addon/lint/lint.js";
import "codemirror/addon/lint/json-lint.js";
import "codemirror/keymap/sublime.js";
import "codemirror/addon/merge/merge.js";

// Stylesheets
import "codemirror/lib/codemirror.css";
import "codemirror/theme/monokai.css";
import "codemirror/addon/lint/lint.css";
import "codemirror/addon/merge/merge.css";
import "./assets/scss/element-variables.scss";
import "./assets/scss/global.scss";

import VueClipboard from "vue-clipboard2";
import { initSentry } from "@/sentry";
import { initStoreWatcher } from "@/helpers/storeWatcher";
import Worker from "worker-loader!@/workers/index.ts";

/**
 * Web Worker instance for background processing.
 * Used to offload heavy computations from the main thread.
 *
 * @type {Worker}
 */
const worker = new Worker();

/**
 * Clickjacking protection: Only display content if not in an iframe.
 * Prevents the dashboard from being embedded in malicious frames.
 */
if (self == top) {
  document.documentElement!.style.display = "block";
}

// Disable Vue production tip in development mode
Vue.config.productionTip = process.env.NODE_ENV !== "production";

/**
 * Global release identifier for version tracking and Sentry integration.
 * Format: "name@version" from version.json
 *
 * @type {string}
 * @global
 */
window.DASHBOARD_RELEASE_ID = `${process.env.NAME}@${process.env.VERSION}`;

/**
 * Initializes the authentication plugin with Auth0 configuration.
 *
 * Sets up the AuthPlugin with default Auth0 credentials. The actual provider
 * (Auth0 or Keycloak) is determined at runtime by the server's auth-type endpoint.
 *
 * @returns {void}
 *
 * @see {@link module:auth} for authentication implementation details
 */
const initAuth = () => {
  const clientId = "a7ZRp2UutRceg2gUA23g7nvx9llXRNir";
  const domain = "keyreply.auth0.com";
  const audience = "https://login.keyreply.com";
  const useRefreshTokens = true;

  Vue.use(AuthPlugin, {
    domain,
    clientId,
    audience,
    useRefreshTokens,
    store,
  });
};

/**
 * Initializes all Vue plugins and global registrations.
 *
 * Registers the following plugins:
 * - VueHtml5Editor: Rich text editor with custom createid module
 * - ElementUI: UI component library (English locale)
 * - ElSearchTablePagination: Searchable table component
 * - VueCodeMirror: Code editor component
 * - Vuelidate: Form validation
 * - ReadMore: Text truncation component
 * - Viewer: Image viewer
 * - VueMasonry: Masonry grid layout
 * - KeyReplyUtility: Custom utility plugins
 * - VueDND: Drag and drop
 * - VueTimeago: Relative time display
 * - VueClipboard: Clipboard operations
 *
 * Also registers global filters and directives from their respective modules.
 *
 * @returns {void}
 */
const initVuePlugins = () => {
  // HTML5 Editor with custom ID creation module for element targeting
  Vue.use(VueHtml5Editor, {
    icons: {
      text: "fas fa-pencil-alt",
      color: "fa fa-paint-brush",
      font: "fa fa-font",
      align: "fa fa-align-justify",
      list: "fa fa-list",
      link: "fas fa-link",
      unlink: "fas fa-unlink",
      tabulation: "fa fa-table",
      image: "fas fa-images",
      hr: "fa fa-minus",
      eraser: "fa fa-eraser",
      undo: "fa-undo fa",
      "full-screen": "fa fa-arrows-alt",
      info: "fa fa-info",
    },
    modules: [
      {
        name: "createid",
        icon: "fas fa-hashtag",
        i18n: "createid",
        show: true,
        dashboard: {
          data() {
            return {
              input: null,
            };
          },
          template: `
          <div style="display:flex;">
            <div style="max-width: 300px;" class="el-input el-input--mini">
              <input class="el-input__inner" placeholder="Insert id for the highlighted element." v-model="input"  />
            </div>    
            <button @click.prevent="setId">Update</button>
          </div>
          `,
          methods: {
            setId() {
              const vm = this as any;
              if (vm.input) {
                vm.$parent.execCommand("setId", vm.input);
                vm.input = null;
              }
            },
          },
        },
      },
    ],
  });
  Vue.use(ElementUI, { locale });
  Vue.use(ElSearchTablePagination);
  Vue.use(VueCodeMirror);
  Vue.use(Vuelidate);
  Vue.use(ReadMore);
  Vue.use(Viewer);
  Vue.use(VueMasonry);
  Vue.use(KeyReplyUtility);
  Vue.use(VueDND);
  Vue.use(VueTimeago, {
    name: "Timeago",
    locale: "en",
  });
  Vue.use(VueClipboard);

  // Register global utility filters.
  _.forEach(filters, (definition, id) => Vue.filter(id, definition));

  // Register global utility directives.
  _.forEach(directives, (definition, id) => Vue.directive(id, definition));
};

// Initialize all services in the correct order
initAuth();           // Authentication must be first
initVuePlugins();     // Register all Vue plugins
initSentry();         // Error tracking and monitoring
initStoreWatcher(store); // Store state change observer

/**
 * Exports for use in main.ts
 *
 * @exports Vue - Configured Vue constructor with all plugins
 * @exports worker - Web Worker instance for background processing
 */
export { Vue, worker };
